import { Location } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FirmContactService } from '@aiwyn/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subject,
} from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import {
  DEFAULT_SNACKBAR_CONFIG,
  LoadableStatus,
  SnackbarComponent,
  SnackbarType,
} from '@aiwyn/shared';
import { TaxPaymentResponse, TaxPaymentsApiService } from '@aiwyn/api';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { LogRocketService } from '../../../../config/services/log-rocket.service';
import { PendoService } from '../../../../auth/services/pendo.service';
import { GetTaxPayments, SettingsState } from '../../settings/state';
import { ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_PAYMENTS } from '../../../../routing-constants';
import { TaxPaymentUi, toTaxPaymentUi } from './models/tax-payment-ui';

/** Status filter values mirror the `displayStatus` enum on TaxPaymentUi, plus 'ALL'. */
export type TaxPaymentStatusFilter =
  | 'ALL'
  | 'AWAITING_TAXPAYER_SETUP'
  | 'OVERDUE'
  | 'DUE_SOON'
  | 'UPCOMING'
  | 'PROCESSING'
  | 'PROCESSED'
  | 'CANCELLED';

/**
 * Status priority for sortable-header sort by status. Lower = more urgent;
 * sort ASC puts the most urgent items at the top.
 */
const STATUS_SORT_PRIORITY: Record<string, number> = {
  OVERDUE: 0,
  DUE_SOON: 1,
  UPCOMING: 2,
  PROCESSING: 3,
  // Awaiting-setup rows sit between active and processed: they're inert until
  // the taxpayer completes Remitian onboarding, but they're a forward-looking
  // commitment so they shouldn't fall to the bottom alongside cancelled rows.
  AWAITING_TAXPAYER_SETUP: 4,
  PROCESSED: 5,
  CANCELLED: 6,
};

/**
 * Display config for the metric cards row above the table. Keeps card colors
 * aligned with the status pill palette without re-hardcoding hex everywhere;
 * the SCSS resolves the actual swatch from the badge variant.
 */
export interface MetricCardConfig {
  readonly status: TaxPaymentStatusFilter;
  readonly label: string;
  readonly badgeVariant: 'error' | 'warning' | 'primary' | 'success' | 'muted' | 'info';
}

// Mirrors firm-portal's tile set so the two portals tell the same story about
// where a payment is in its lifecycle. Pilot feedback: the secondary status
// `<select>` was redundant with the tile filters, and Cancelled needed its
// own tile alongside the others.
export const METRIC_CARDS: ReadonlyArray<MetricCardConfig> = [
  { status: 'AWAITING_TAXPAYER_SETUP', label: 'Awaiting setup', badgeVariant: 'muted' },
  { status: 'OVERDUE', label: 'Overdue', badgeVariant: 'error' },
  { status: 'DUE_SOON', label: 'Due Soon', badgeVariant: 'warning' },
  { status: 'UPCOMING', label: 'Upcoming', badgeVariant: 'primary' },
  { status: 'PROCESSING', label: 'Processing', badgeVariant: 'info' },
  { status: 'PROCESSED', label: 'Processed', badgeVariant: 'success' },
  { status: 'CANCELLED', label: 'Cancelled', badgeVariant: 'muted' },
];

/**
 * Tax Payments section on the client portal Payments page.
 *
 * Renders a Material table (or stacked cards on mobile) of tax payments returned
 * by `TaxPaymentsApiService.getTaxPayments()`. Each payable row exposes a Pay
 * Now button that requests a short-lived Remitian passwordless URL and
 * navigates the current tab to it.
 *
 * V1.5 additions (this component):
 *   - Status filter dropdown in the section header (AIW-799).
 *   - Click-to-filter metric cards above the table (AIW-800).
 *   - Sortable headers on Due Date, Amount, Status (AIW-801).
 *   - Co-branded gradient empty state (AIW-802).
 *
 * Section visibility is gated on the `ENABLE_REMITIAN_TAX_PAYMENTS` LD flag
 * by the parent `<app-payments>`; this component itself assumes it should
 * fetch and render data when mounted.
 */
@Component({
  selector: 'app-tax-payments',
  templateUrl: './tax-payments.component.html',
  styleUrls: ['./tax-payments.component.scss'],
  standalone: false,
})
export class TaxPaymentsComponent implements OnInit, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();
  // _unsubscribe$ only cancels Observable subscriptions; `firstValueFrom`
  // converts to a Promise that ignores it. Guards in async handlers consult
  // this flag before firing window.open or analytics events after navigation.
  private _destroyed = false;

  /**
   * Track active pay-now requests by `pmsRef` so each row can render its own
   * spinner without blocking sibling rows.
   */
  payNowLoadingRefs = new Set<string>();

  /**
   * Loading flag for the "Connect Remitian" CTA. The button mints a Remitian
   * passwordless URL on click (via the same `pay-now` endpoint used by
   * payable rows) and navigates the current tab to it.
   */
  connectRemitianLoading = false;

  /** Loading flag for the "Go to Remitian" CTA, parallel to `connectRemitianLoading`. */
  goToRemitianLoading = false;

  /**
   * Marker query param the Remitian portal redirects back with after Bruce
   * completes onboarding. Watched in `ngOnInit` so the component can clear
   * the param and refresh the tax-payments query — backend reconciliation
   * then picks up the now-linked taxpayer + activates the queued payment.
   */
  private static readonly REMITIAN_RETURN_PARAM = 'remitian-connected';

  readonly columnsToDisplay: ReadonlyArray<string> = [
    'account',
    'description',
    'taxAuthority',
    'paymentDate',
    'amount',
    'status',
    'payNow',
  ];

  readonly LoadableStatus = LoadableStatus;

  readonly metricCards = METRIC_CARDS;

  /**
   * Backing data source for the desktop `<mat-table>`. Filter + sort + paginator
   * cascade is wired up in `ngOnInit` / `ngAfterViewInit`. Filter is the
   * selected status (set via `dataSource.filter`); sort is wired through
   * `MatSort` with a custom accessor for status priority.
   */
  readonly dataSource = new MatTableDataSource<TaxPaymentUi>([]);

  // The <mat-paginator> and <mat-sort> are gated behind multiple *ngIfs
  // (LOADED state + non-empty data + desktop viewport) and don't exist when
  // ngAfterViewInit runs. Setter-based ViewChilds rebind the dataSource the
  // moment Angular renders the elements, so the paginator footer reflects
  // the actual row count instead of "0 of 0".
  private _paginator?: MatPaginator;
  @ViewChild(MatPaginator) set paginator(p: MatPaginator | undefined) {
    this._paginator = p;
    if (p) this.dataSource.paginator = p;
  }
  get paginator(): MatPaginator | undefined { return this._paginator; }

  private _sort?: MatSort;
  @ViewChild(MatSort) set sort(s: MatSort | undefined) {
    this._sort = s;
    if (s) this.dataSource.sort = s;
  }
  get sort(): MatSort | undefined { return this._sort; }

  /**
   * Selected status filter, both for the dropdown and the metric-card click
   * affordance. `ALL` means no filter; any other value narrows by exact
   * `displayStatus` match. Public so the template can two-way bind a select
   * via `[ngModel]="(selectedStatus$ | async)" (ngModelChange)="onStatusFilterChange($event)"`.
   */
  private readonly _selectedStatus$ = new BehaviorSubject<TaxPaymentStatusFilter>('ALL');
  readonly selectedStatus$ = this._selectedStatus$.asObservable();

  taxPayments$: Observable<TaxPaymentUi[]> = this.store.select(SettingsState.getTaxPayments).pipe(
    map(payments => (payments ?? []).map(toTaxPaymentUi)),
  );

  /**
   * Whether to render the top-of-page "Connect Remitian" banner. True when at
   * least one row is in `AWAITING_TAXPAYER_SETUP` — that's the backend's
   * signal that a payment was scheduled before this taxpayer onboarded. The
   * frontend doesn't need a separate REMITIAN_TAXPAYER linkage probe: if the
   * taxpayer is fully linked, the backend's reconciliation has already flipped
   * those rows to UPCOMING so this selector returns false naturally.
   */
  // Gated by enable_remitian_invite_and_queue. Default OFF — the banner and
  // greyed-out pending rows only render when the firm has opted into the
  // schedule-before-onboard flow.
  private readonly inviteAndQueueEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(
    LaunchDarklyFlag.ENABLE_REMITIAN_INVITE_AND_QUEUE,
  );

  readonly showConnectRemitianBanner$: Observable<boolean> = combineLatest([
    this.taxPayments$,
    this.inviteAndQueueEnabled$,
  ]).pipe(
    map(([payments, flagEnabled]) =>
      flagEnabled && payments.some(p => p.displayStatus === 'AWAITING_TAXPAYER_SETUP'),
    ),
    distinctUntilChanged(),
  );

  /**
   * True when the flag is on, the taxpayer has at least one tax payment, and
   * none are in `AWAITING_TAXPAYER_SETUP`. Shown mutually exclusively with
   * `showConnectRemitianBanner$`: once all pending rows flip to UPCOMING (post-
   * onboarding reconciliation), this banner replaces the Connect one.
   */
  readonly showGoToRemitianBanner$: Observable<boolean> = combineLatest([
    this.inviteAndQueueEnabled$,
    this.taxPayments$,
  ]).pipe(
    map(([flagEnabled, payments]) =>
      flagEnabled && payments.length > 0 && !payments.some(p => p.displayStatus === 'AWAITING_TAXPAYER_SETUP'),
    ),
    distinctUntilChanged(),
  );

  /**
   * First pmsRef among rows in `AWAITING_TAXPAYER_SETUP`. The Connect Remitian
   * click handler routes through the existing `pay-now` endpoint to mint a
   * Remitian passwordless URL — we just need any pending row's id; the
   * backend's passwordless flow lands the user in the same Remitian setup
   * surface regardless of payment context.
   */
  private readonly pendingSetupPmsRef$: Observable<string | undefined> = this.taxPayments$.pipe(
    map(payments => payments.find(p => p.displayStatus === 'AWAITING_TAXPAYER_SETUP')?.pmsRef),
  );

  /**
   * First non-cancelled pmsRef across all rows. The Go to Remitian handler
   * needs any valid row id to reach the pay-now endpoint; preference for a
   * non-cancelled row avoids pointing at a stale Remitian payment context.
   * Falls back to any row if everything is cancelled.
   */
  private readonly firstPmsRef$: Observable<string | undefined> = this.taxPayments$.pipe(
    map(payments => {
      const active = payments.find(p => p.displayStatus !== 'CANCELLED');
      return (active ?? payments[0])?.pmsRef;
    }),
  );

  /**
   * Filtered tax payments stream used by the mobile-stacked-cards layout.
   * Desktop reads filtered rows from `dataSource` (paginated + sorted); mobile
   * iterates this stream directly. Sort isn't applied to mobile because the
   * card layout doesn't expose sortable headers.
   */
  readonly filteredTaxPayments$: Observable<TaxPaymentUi[]> = combineLatest([
    this.taxPayments$,
    this._selectedStatus$,
  ]).pipe(
    map(([payments, status]) =>
      status === 'ALL' ? payments : payments.filter(p => p.displayStatus === status),
    ),
  );

  /**
   * Per-status counts shown on the metric cards. Computed from the unfiltered
   * stream so the card values stay stable when a filter is active (otherwise
   * clicking Overdue would show "Overdue: 3" while the others show 0, which
   * defeats the at-a-glance summary purpose).
   */
  readonly metricCounts$: Observable<Record<string, number>> = this.taxPayments$.pipe(
    map(payments => this.countByDisplayStatus(payments)),
  );

  status$: Observable<LoadableStatus> = this.store.select(SettingsState.getTaxPaymentsStatus);

  readonly firmContact = inject(FirmContactService);

  constructor(
    public readonly configService: ConfigService,
    private readonly store: Store,
    private readonly taxPaymentsApiService: TaxPaymentsApiService,
    private readonly logRocketService: LogRocketService,
    private readonly pendoService: PendoService,
    private readonly snackbar: MatSnackBar,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly location: Location,
  ) {
    // Filter predicate runs on the desktop dataSource; called by Material when
    // `dataSource.filter` is set. The filter string is the status enum value
    // (or empty for ALL). Exact match avoids substring surprises (e.g. PROCESSED
    // would otherwise match "PROCESSING").
    this.dataSource.filterPredicate = (row, statusFilter): boolean => {
      if (!statusFilter) return true;
      return row.displayStatus === statusFilter;
    };
    // Sort accessor: status sorts by priority, paymentDate as Date timestamp,
    // amount as a number. Other columns fall through to the row property.
    this.dataSource.sortingDataAccessor = (row, column) => {
      switch (column) {
        case 'status':
          return STATUS_SORT_PRIORITY[row.displayStatus] ?? 99;
        case 'paymentDate':
          return new Date(row.paymentDate).getTime();
        case 'amount':
          return row.amount;
        case 'account':
          return (row.taxpayerName ?? '').toLowerCase();
        default:
          return (row as unknown as Record<string, string | number>)[column] ?? '';
      }
    };
  }

  ngOnInit(): void {
    // If the URL carries `?remitian-connected=1`, the user just bounced back
    // from the Remitian onboarding flow. Strip the marker (don't leave it
    // visible in the address bar across reloads) and force-refresh the
    // tax-payments query so backend reconciliation has a clean slate to flip
    // any `AWAITING_TAXPAYER_SETUP` rows to `UPCOMING` on the next read.
    const returnedFromRemitian = this.consumeRemitianReturnParam();
    this.store.dispatch(new GetTaxPayments(returnedFromRemitian))
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe();

    // Push every store update into the table data source so the paginator
    // reflects the latest row count. Filter + sort cascade through
    // MatTableDataSource automatically.
    this.taxPayments$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((rows) => {
        this.dataSource.data = rows;
      });

    // Apply the status filter to the desktop dataSource. Mobile reads the
    // filtered stream directly via `filteredTaxPayments$`.
    this._selectedStatus$
      .pipe(distinctUntilChanged(), takeUntil(this._unsubscribe$))
      .subscribe((status) => {
        this.dataSource.filter = status === 'ALL' ? '' : status;
        // Reset to first page when the filter changes so the user always
        // sees results from the top of the filtered set.
        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
      });

    // Belt-and-suspenders refresh on tab re-focus. Connect Remitian / Pay Now
    // now navigate the SAME tab to Remitian, so the primary refresh path is
    // Remitian's post-verification redirect back with ?remitian-connected=1
    // (consumed in consumeRemitianReturnParam). But if the user instead bounces
    // back via the browser's back button, a restored bfcache page, or a hand-
    // opened second tab, this listener still force-refreshes when the tab
    // becomes visible so reconciliation's flips (AWAITING_TAXPAYER_SETUP →
    // UPCOMING) show and the banner clears. We force because the Loadable cache
    // gate (`shouldRequest` returns false for LOADED) makes a plain
    // GetTaxPayments a no-op once the section has loaded.
    document.addEventListener('visibilitychange', this.handleVisibilityRefresh);
    window.addEventListener('focus', this.handleWindowFocusRefresh);

    // Section-viewed analytics. The parent `<app-payments>` only renders this
    // component when `ENABLE_REMITIAN_TAX_PAYMENTS` resolves true, so reaching
    // `ngOnInit` is itself the "flag on" signal. We wait for the first LOADED
    // status so the payload reflects real backend counts (total + per-status
    // breakdown). Without that gate the selector emits an empty `[]` during
    // LOADING, which would skew the funnel toward "empty section" telemetry.
    combineLatest([this.status$, this.taxPayments$]).pipe(
      filter(([status]) => status === LoadableStatus.LOADED),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe(([, payments]) => {
      this.pendoService.track('client_tax_payments_section_viewed', {
        count: payments.length,
        countByDisplayStatus: this.countByDisplayStatus(payments),
      });
    });
  }

  /**
   * `visibilitychange` handler. Fires on both hide and show; we only refresh
   * when the tab becomes visible again (the user came back from the Remitian
   * tab). Arrow function so `removeEventListener` in `ngOnDestroy` unbinds the
   * exact same reference.
   */
  private readonly handleVisibilityRefresh = (): void => {
    if (document.visibilityState === 'visible') {
      this.forceRefreshTaxPayments();
    }
  };

  /**
   * `window.focus` handler. Complements `visibilitychange` for browsers/flows
   * where focus returns to the tab without a visibility transition.
   */
  private readonly handleWindowFocusRefresh = (): void => {
    this.forceRefreshTaxPayments();
  };

  /**
   * Force a tax-payments refetch, bypassing the Loadable cache gate. The
   * `forceRefresh` flag is required: `GetTaxPayments()` short-circuits once the
   * section is LOADED, so the banner/list would never reflect post-onboarding
   * reconciliation without it.
   */
  private forceRefreshTaxPayments(): void {
    this.store.dispatch(new GetTaxPayments(true))
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe();
  }

  /**
   * Build a `{ status: count }` map. Used both for the section-viewed Pendo
   * payload and for the metric cards row.
   */
  private countByDisplayStatus(payments: TaxPaymentUi[]): Record<string, number> {
    return payments.reduce<Record<string, number>>((acc, p) => {
      const key = p.displayStatus;
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    document.removeEventListener('visibilitychange', this.handleVisibilityRefresh);
    window.removeEventListener('focus', this.handleWindowFocusRefresh);
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /**
   * Track-by for `*ngFor` and `mat-table`. `pmsRef` is the public Aiwyn ID and
   * is guaranteed unique per row.
   */
  trackByPmsRef = (_: number, row: TaxPaymentUi): string => row.pmsRef;

  /** Track-by for the metric cards row. Status is unique per card. */
  trackByMetricStatus = (_: number, card: MetricCardConfig): string => card.status;

  /**
   * Status filter dropdown change handler. Fires Pendo on every change for
   * the funnel (so we can see which filters users actually use).
   */
  onStatusFilterChange(status: TaxPaymentStatusFilter): void {
    this._selectedStatus$.next(status);
    this.pendoService.track('client_tax_payments_filter_changed', { filter: status });
  }

  /**
   * Metric card click handler. Toggle behavior: clicking the card for the
   * currently-active status clears the filter (back to ALL); clicking any
   * other card sets the filter to that status.
   */
  onMetricCardClick(status: TaxPaymentStatusFilter): void {
    const current = this._selectedStatus$.value;
    const next: TaxPaymentStatusFilter = current === status ? 'ALL' : status;
    this._selectedStatus$.next(next);
    this.pendoService.track('client_tax_payments_metric_card_clicked', {
      status,
      resultedIn: next,
    });
  }

  /**
   * Sort change handler. Fires Pendo for the funnel; the actual sort is
   * applied by Material via the `MatSort` directive.
   */
  onSortChange(column: string, direction: '' | 'asc' | 'desc'): void {
    this.pendoService.track('client_tax_payments_sort_changed', {
      column,
      direction: direction || 'cleared',
    });
  }

  /**
   * Pay Now click flow: ask the backend for a short-lived Remitian URL, fire
   * analytics events, and navigate the current tab to it. Same-tab navigation
   * lets Remitian's post-verification redirect land back on this tab. Failures
   * surface a snackbar but never throw — analytics is intentionally swallowed
   * so a tracking failure cannot block the user.
   */
  async onPayNowClick(taxPayment: TaxPaymentUi): Promise<void> {
    if (this.payNowLoadingRefs.has(taxPayment.pmsRef)) {
      return;
    }
    this.payNowLoadingRefs.add(taxPayment.pmsRef);

    this.pendoService.track('client_tax_payment_pay_now_clicked', {
      pmsRef: taxPayment.pmsRef,
      taxAuthority: taxPayment.taxAuthority,
    });

    try {
      // Don't pass redirectUrl on Pay Now. Remitian's auth-passwordless honors
      // redirectUrl right after Phone Verification — which short-circuits the
      // payment authorization step that lives between OTP and the bounce-back.
      // Pilot users were landing back on client-portal before they ever saw the
      // Remitian "Authorize Payment" screen. Without redirectUrl, OTP completes,
      // the taxpayer lands in Remitian's dashboard, authorizes the payment, and
      // returns to Aiwyn at their own pace. Connect Remitian still passes one
      // because that flow has no post-OTP step to interrupt.
      const response = await firstValueFrom(this.taxPaymentsApiService.getPayNowUrl(taxPayment.pmsRef, undefined));
      // Component may have been destroyed mid-request (user navigated away).
      // _unsubscribe$ doesn't cancel firstValueFrom's Promise, so guard here
      // to avoid an unwanted navigation + stale Pendo events.
      if (this._destroyed) return;
      // Guard against a null/empty URL so the same-tab navigation can't strand
      // the user on a blank page.
      if (!response?.url) {
        this.snackbar.openFromComponent(SnackbarComponent, {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: 'Unable to open payment portal. Please try again.',
            type: SnackbarType.ERROR,
          },
        });
        return;
      }
      this.pendoService.track('client_tax_payment_remitian_opened', {
        pmsRef: taxPayment.pmsRef,
        nextStep: response.nextStep,
      });
      // Navigate the SAME tab (not window.open). Remitian's post-verification
      // redirect (?remitian-connected=1) then lands back on this original
      // client-portal tab, where ngOnInit's return-param handler refetches and
      // clears the banner. Opening a new tab stranded the bounce-back in the
      // popup and left the original tab stale.
      window.location.href = response.url;
    } catch (err) {
      // Logged via the LogRocket-aware logger pipeline (see LogRocketLogger).
      // Avoid leaking the raw error through the snackbar — the backend's 4xx
      // bodies may include identifying info we don't want surfaced in the UI.
      this.logRocketService.captureSessionUrl();
      // eslint-disable-next-line no-console
      console.error('[TaxPaymentsComponent] pay-now failed', { pmsRef: taxPayment.pmsRef, err });

      // Surface the failure into the funnel so we can distinguish "user
      // bounced from Remitian" (no failed event, no opened event) from
      // "infrastructure failed" (failed event fired). HttpErrorResponse
      // exposes `status` (HTTP) and an `error.code` body field; we sniff
      // generously because the REST client may also surface plain Errors.
      const status = (err as { status?: number })?.status;
      const errorCode = (err as { error?: { code?: string } })?.error?.code
        ?? (err as { code?: string })?.code;
      this.pendoService.track('client_tax_payment_pay_now_failed', {
        pmsRef: taxPayment.pmsRef,
        taxAuthority: taxPayment.taxAuthority,
        status,
        errorCode,
      });

      this.snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'Unable to open payment portal. Please try again.',
          type: SnackbarType.ERROR,
        },
      });
    } finally {
      this.payNowLoadingRefs.delete(taxPayment.pmsRef);
    }
  }

  isPayNowLoading(taxPayment: TaxPaymentUi): boolean {
    return this.payNowLoadingRefs.has(taxPayment.pmsRef);
  }

  /**
   * "Connect Remitian" click handler. Picks the first pending-setup row, mints
   * a Remitian passwordless URL via the existing `pay-now` endpoint, and
   * navigates the current tab to it. Remitian's portal lands the taxpayer in
   * the same setup surface regardless of payment context — the row id is just
   * the addressing mechanism to reach the backend's passwordless flow.
   *
   * After the taxpayer finishes onboarding, Remitian redirects them back to
   * client-portal with `?remitian-connected=1`, which `ngOnInit` watches for
   * and uses to force-refresh the tax-payments query.
   */
  async onConnectRemitianClick(): Promise<void> {
    if (this.connectRemitianLoading) {
      return;
    }
    const pendingPmsRef = await firstValueFrom(this.pendingSetupPmsRef$.pipe(take(1)));
    if (!pendingPmsRef) {
      return;
    }
    this.connectRemitianLoading = true;

    this.pendoService.track('client_tax_payment_connect_remitian_clicked', {
      pmsRef: pendingPmsRef,
    });

    try {
      const redirectUrl = this.buildRemitianReturnUrl();
      const response = await firstValueFrom(
        this.taxPaymentsApiService.getPayNowUrl(pendingPmsRef, redirectUrl),
      );
      if (this._destroyed) return;
      // Guard against a null/empty URL so the same-tab navigation can't strand
      // the user on a blank page.
      if (!response?.url) {
        this.snackbar.openFromComponent(SnackbarComponent, {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: 'Unable to open Remitian setup. Please try again.',
            type: SnackbarType.ERROR,
          },
        });
        return;
      }
      // Navigate the SAME tab (not window.open). The redirectUrl above encodes
      // ?remitian-connected=1, so Remitian's post-onboarding redirect lands back
      // on this original tab where ngOnInit force-refreshes and clears the
      // banner. Opening a new tab kept the bounce-back in the popup and left the
      // original tab showing the stale "Connect Remitian" banner.
      window.location.href = response.url;
    } catch (err) {
      this.logRocketService.captureSessionUrl();
      // eslint-disable-next-line no-console
      console.error('[TaxPaymentsComponent] connect-remitian failed', { pmsRef: pendingPmsRef, err });

      const status = (err as { status?: number })?.status;
      const errorCode = (err as { error?: { code?: string } })?.error?.code
        ?? (err as { code?: string })?.code;
      this.pendoService.track('client_tax_payment_connect_remitian_failed', {
        pmsRef: pendingPmsRef,
        status,
        errorCode,
      });

      this.snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'Unable to open Remitian setup. Please try again.',
          type: SnackbarType.ERROR,
        },
      });
    } finally {
      this.connectRemitianLoading = false;
    }
  }

  /**
   * "Go to Remitian" click handler. Used when the taxpayer is already connected
   * (no `AWAITING_TAXPAYER_SETUP` rows). Picks any active payment row's pmsRef,
   * mints a Remitian passwordless URL without a redirectUrl (so the taxpayer
   * stays in Remitian to manage or pay), and navigates the current tab to it.
   * Mirrors the navigation contract of `onPayNowClick` exactly.
   */
  async onGoToRemitianClick(): Promise<void> {
    if (this.goToRemitianLoading) {
      return;
    }
    const pmsRef = await firstValueFrom(this.firstPmsRef$.pipe(take(1)));
    if (!pmsRef) {
      return;
    }
    this.goToRemitianLoading = true;

    this.pendoService.track('client_tax_payment_go_to_remitian_clicked', { pmsRef });

    try {
      const response = await firstValueFrom(this.taxPaymentsApiService.getPayNowUrl(pmsRef, undefined));
      if (this._destroyed) return;
      if (!response?.url) {
        this.snackbar.openFromComponent(SnackbarComponent, {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: 'Unable to open Remitian. Please try again.',
            type: SnackbarType.ERROR,
          },
        });
        return;
      }
      this.pendoService.track('client_tax_payment_go_to_remitian_opened', {
        pmsRef,
        nextStep: response.nextStep,
      });
      window.location.href = response.url;
    } catch (err) {
      this.logRocketService.captureSessionUrl();
      // eslint-disable-next-line no-console
      console.error('[TaxPaymentsComponent] go-to-remitian failed', { pmsRef, err });

      const status = (err as { status?: number })?.status;
      const errorCode = (err as { error?: { code?: string } })?.error?.code
        ?? (err as { code?: string })?.code;
      this.pendoService.track('client_tax_payment_go_to_remitian_failed', {
        pmsRef,
        status,
        errorCode,
      });

      this.snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'Unable to open Remitian. Please try again.',
          type: SnackbarType.ERROR,
        },
      });
    } finally {
      this.goToRemitianLoading = false;
    }
  }

  /**
   * Build the URL Remitian should redirect back to once Bruce completes
   * onboarding. We append `?remitian-connected=1` as the bounce-back marker;
   * `ngOnInit` strips it and force-refreshes the tax-payments query.
   */
  private buildRemitianReturnUrl(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    // Land on /payments so TaxPaymentsComponent's ngOnInit runs and
    // consumeRemitianReturnParam() can strip the flag + force-refresh.
    // `Location.prepareExternalUrl` folds in the `<base href="/client-portal">`
    // so the route resolves under the actual deploy base — without it the
    // bounce-back hits `/payments` (no `/client-portal/main/` prefix) and the
    // router falls through to the invoices home, silently no-op'ing refresh.
    const tree = this.router.createUrlTree(
      [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_PAYMENTS],
      { queryParams: { [TaxPaymentsComponent.REMITIAN_RETURN_PARAM]: 1 } },
    );
    return `${window.location.origin}${this.location.prepareExternalUrl(this.router.serializeUrl(tree))}`;
  }

  /**
   * Detect the `?remitian-connected=1` return marker, strip it from the URL,
   * and return whether the page was loaded as part of a Remitian return trip
   * (so the caller can request a force-refresh of the tax-payments query).
   */
  private consumeRemitianReturnParam(): boolean {
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (!params.has(TaxPaymentsComponent.REMITIAN_RETURN_PARAM)) {
      return false;
    }
    // Strip the marker so it doesn't survive across reloads / shareable URLs.
    // queryParamsHandling: '' clears all current params; we only had the
    // remitian-connected marker so this is a clean reset. Preserve route via
    // a relative navigation off the current ActivatedRoute.
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      queryParamsHandling: '',
      replaceUrl: true,
    });
    this.pendoService.track('client_tax_payment_remitian_return_detected', {});
    return true;
  }
}
